import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageSquare, PhoneCall, X, Loader2, CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { useSettings } from "../../lib/useSettings";
import { api, track } from "../../lib/api";
import { toast } from "sonner";

const PROJECTS = [
  "Climatisation gainable",
  "Climatisation split / multisplit",
  "Pompe à chaleur",
  "Climatisation professionnelle (entreprise)",
  "Dépannage climatisation",
  "Entretien climatisation",
  "Plomberie - installation",
  "Plomberie - dépannage",
  "Chauffe-eau / cumulus",
  "Autre",
];

export default function FloatingActions() {
  const { settings: s } = useSettings();
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    civility: "M.", name: "", phone: "", email: "", postal_code: "", project: "", message: "",
  });
  const rootRef = useRef(null);
  const navigate = useNavigate();
  const openedAt = useRef(Date.now());
  const hpWebsite = useRef("");
  const hpEmail2 = useRef("");

  // Periodic shake trigger — every 8s, gentle
  useEffect(() => {
    const t = setTimeout(() => setShouldShake(true), 3000);
    return () => clearTimeout(t);
  }, []);

  // Click outside closes the expanded panel
  useEffect(() => {
    const onClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setExpanded(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
    if (modalOpen) openedAt.current = Date.now();
    return () => { document.body.style.overflow = ""; };
  }, [modalOpen]);

  const openCallback = () => { setExpanded(false); setSent(false); setModalOpen(true); };
  const goDevis = () => { setExpanded(false); navigate("/devis"); };
  const callPhone = () => { track("tel_click", { meta: { location: "fab" } }); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Nom et téléphone obligatoires");
    const phoneClean = form.phone.replace(/\s/g, "");
    if (!/^(?:\+33|0)[\s.-]?[1-9](?:[\s.-]?\d{2}){4}$/.test(phoneClean)) {
      return toast.error("Numéro de téléphone invalide (format FR attendu)");
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        name: `${form.civility} ${form.name}`.trim(),
        phone: form.phone, email: form.email, zone: form.postal_code,
        service: form.project, message: form.message, type: "rappel",
        meta: { civility: form.civility, postal_code: form.postal_code },
        hp_website: hpWebsite.current,
        hp_email2: hpEmail2.current,
        ts_open: openedAt.current,
        referrer: document.referrer || "",
        page: window.location.pathname,
      });
      await track("devis_submit", { meta: { source: "fab-callback", project: form.project } });
      setSent(true);
      toast.success("Demande envoyée — nous vous rappelons sous 1h ouvrée.");
    } catch { toast.error("Erreur d'envoi. Réessayez ou appelez-nous."); }
    finally { setLoading(false); }
  };

  const reset = () => { setSent(false); setForm({ civility: "M.", name: "", phone: "", email: "", postal_code: "", project: "", message: "" }); };

  if (!s) return null;
  const phoneClean = (s.phone || "").replace(/\s/g, "");

  return (
    <>
      <div
        ref={rootRef}
        className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-40 flex flex-col items-end gap-3"
        data-testid="fab-root"
      >
        {/* Expandable action chips */}
        <div
          className={`flex flex-col items-end gap-2.5 transition-all duration-300 ${
            expanded ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
          }`}
        >
          {s.phone && (
            <a
              href={`tel:${phoneClean}`}
              onClick={callPhone}
              className="flex items-center gap-3 pl-5 pr-5 py-3 rounded-full bg-[color:var(--brand-red)] text-white font-bold shadow-[0_12px_30px_-8px_rgba(227,6,19,0.6)] hover:shadow-[0_16px_40px_-8px_rgba(227,6,19,0.7)] hover:-translate-x-0.5 transition-all text-sm"
              style={{ transitionDelay: expanded ? "0ms" : "120ms" }}
              data-testid="fab-phone"
            >
              <Phone className="w-4 h-4" strokeWidth={2.5} />
              <span>Appeler {s.phone}</span>
            </a>
          )}
          <button
            onClick={goDevis}
            className="flex items-center gap-3 pl-5 pr-5 py-3 rounded-full bg-[color:var(--brand-sky)] text-white font-bold shadow-[0_12px_30px_-8px_rgba(61,185,232,0.6)] hover:shadow-[0_16px_40px_-8px_rgba(61,185,232,0.7)] hover:-translate-x-0.5 transition-all text-sm"
            style={{ transitionDelay: expanded ? "60ms" : "60ms" }}
            data-testid="fab-devis"
          >
            <MessageSquare className="w-4 h-4" strokeWidth={2.5} />
            <span>Devis gratuit</span>
          </button>
          <button
            onClick={openCallback}
            className="flex items-center gap-3 pl-5 pr-5 py-3 rounded-full bg-white text-[color:var(--brand-navy)] font-bold shadow-[0_12px_30px_-8px_rgba(30,58,95,0.45)] border border-[color:var(--brand-navy)]/10 hover:-translate-x-0.5 transition-all text-sm"
            style={{ transitionDelay: expanded ? "120ms" : "0ms" }}
            data-testid="fab-callback"
          >
            <PhoneCall className="w-4 h-4" strokeWidth={2.5} />
            <span>Être rappelé</span>
          </button>
        </div>

        {/* Main FAB trigger */}
        <button
          onClick={() => { setExpanded(!expanded); setShouldShake(false); }}
          aria-label={expanded ? "Fermer" : "Ouvrir le menu de contact"}
          aria-expanded={expanded}
          className={`relative flex items-center justify-center w-16 h-16 md:w-[72px] md:h-[72px] rounded-full transition-all duration-300 ${
            expanded
              ? "bg-white text-[color:var(--brand-navy)] rotate-90 shadow-[0_20px_50px_-12px_rgba(30,58,95,0.6)] ring-2 ring-[color:var(--brand-navy)]/10"
              : "bg-gradient-to-br from-[#7DD3FC] via-[#38BDF8] to-[#0EA5E9] text-white shadow-[0_20px_50px_-8px_rgba(56,189,248,0.75)] hover:-translate-y-0.5 hover:shadow-[0_25px_60px_-6px_rgba(56,189,248,0.9)] ring-2 ring-white/30 fab-sky-glow"
          }`}
          data-testid="fab-trigger"
        >
          {/* Animated sonar rings when collapsed */}
          {!expanded && (
            <>
              <span className="absolute inset-0 rounded-full bg-[color:var(--brand-sky)] opacity-40 animate-sonar" />
              <span className="absolute inset-0 rounded-full bg-[color:var(--brand-sky)] opacity-30 animate-sonar" style={{ animationDelay: "1s" }} />
            </>
          )}

          <span className={`relative z-10 ${shouldShake && !expanded ? "animate-shake-soft" : ""}`}>
            {expanded ? (
              <X className="w-6 h-6" strokeWidth={2.5} />
            ) : (
              <Phone className="w-7 h-7 md:w-8 md:h-8 drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]" strokeWidth={2.4} fill="currentColor" fillOpacity={0.08} />
            )}
          </span>
        </button>

        {/* Helper text when collapsed (desktop only) */}
        {!expanded && (
          <div className="hidden md:block absolute right-20 bottom-4 pointer-events-none whitespace-nowrap px-3 py-1.5 rounded-full bg-[color:var(--brand-navy)] text-white text-[11px] font-bold uppercase tracking-wider shadow-lg animate-fade-up" style={{ animationDelay: "4s" }}>
            Besoin d'aide ?
            <span className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-[color:var(--brand-navy)]" />
          </div>
        )}
      </div>

      {/* Callback Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 animate-fade-up" onClick={() => setModalOpen(false)} data-testid="callback-modal">
          <div className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-[color:var(--brand-navy)] text-white p-6 md:p-7">
              <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Fermer" data-testid="callback-close">
                <X className="w-4 h-4" />
              </button>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[color:var(--brand-sky)]/20 border border-[color:var(--brand-sky)]/40 text-[10px] uppercase tracking-wider font-bold mb-3">
                <Clock className="w-3 h-3" /> Rappel sous 1h ouvrée
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold leading-tight">
                Demande de rappel<br />
                <span className="text-[color:var(--brand-sky)]">gratuit & sans engagement</span>
              </h2>
              <p className="mt-2 text-sm text-white/70">Laissez-nous vos coordonnées, nous vous rappelons rapidement.</p>
            </div>
            {sent ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-display text-2xl font-bold text-[color:var(--brand-navy)] mb-2">Bien reçu !</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">Un membre de l'équipe Kloub'Air Plomberie vous rappelle sous 1h ouvrée. Merci de votre confiance.</p>
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => { reset(); setModalOpen(false); }} className="rounded-full bg-[color:var(--brand-navy)]">Fermer</Button>
                  <Button onClick={reset} variant="outline" className="rounded-full">Nouvelle demande</Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 md:p-7 space-y-4" data-testid="callback-form">
                {/* Honeypot — bots fill these, humans won't see them */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                  <label>Site internet<input type="text" name="website" tabIndex="-1" autoComplete="off" onChange={(e) => { hpWebsite.current = e.target.value; }} /></label>
                  <label>Confirmez votre email<input type="email" name="email_confirmation" tabIndex="-1" autoComplete="off" onChange={(e) => { hpEmail2.current = e.target.value; }} /></label>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)]">Civilité</Label>
                  <RadioGroup value={form.civility} onValueChange={(v) => setForm({ ...form, civility: v })} className="flex gap-3 mt-2" data-testid="callback-civility">
                    {["M.", "Mme", "Autre"].map((c) => (
                      <label key={c} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-all text-sm font-medium ${form.civility === c ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5 text-[color:var(--brand-navy)]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--muted-foreground))]"}`}>
                        <RadioGroupItem value={c} className="sr-only" />
                        {c}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <div>
                  <Label htmlFor="cb-name" className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)]">Nom complet *</Label>
                  <Input id="cb-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 h-11 rounded-xl" placeholder="Prénom Nom" data-testid="callback-name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="cb-phone" className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)]">Téléphone *</Label>
                    <Input id="cb-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required className="mt-1.5 h-11 rounded-xl" placeholder="06 XX XX XX XX" data-testid="callback-phone" />
                  </div>
                  <div>
                    <Label htmlFor="cb-postal" className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)]">Code postal</Label>
                    <Input id="cb-postal" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1.5 h-11 rounded-xl tabular-nums" placeholder="13450" maxLength={5} data-testid="callback-postal" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="cb-email" className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)]">Email</Label>
                  <Input id="cb-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" placeholder="vous@exemple.fr" data-testid="callback-email" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)]">Type de projet</Label>
                  <Select value={form.project} onValueChange={(v) => setForm({ ...form, project: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="callback-project"><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                    <SelectContent className="z-[70]">
                      {PROJECTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cb-msg" className="text-[11px] uppercase tracking-wider font-bold text-[color:var(--brand-navy)]">Précisions (facultatif)</Label>
                  <Textarea id="cb-msg" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-xl" placeholder="Décrivez brièvement votre besoin..." data-testid="callback-message" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold text-base mt-2" data-testid="callback-submit">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</> : <><PhoneCall className="w-4 h-4 mr-2" /> Demander un rappel gratuit</>}
                </Button>
                <p className="text-[10px] text-center text-[hsl(var(--muted-foreground))] uppercase tracking-wider flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3 h-3 text-emerald-500" />
                  Protégé anti-spam · RGPD · Données confidentielles
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
